import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Table } from '@components/Common';
import { ContentContainer } from '@components/Common/Loaders/ContentLoader';
import OperationLoader from '@components/Common/Loaders/OperationLoader';
import { HeadingRow, Layout, MainContent, SideBar } from '@components/Common/SideBarLayout';
import ProductEditor from '@components/Pages/Products/ProductEditor';
import ProductMixtureEditor from '@components/Pages/Products/ProductMixtureEditor';
import { useCustomersQuery } from '@api/queries/customers';
import { useTanksQuery } from '@api/queries/tanks';
import { ProductType } from '~types/types';

// TanStack Query hooks for Products
import {
  useProductsQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  Product
} from '@api/queries/products';

// TanStack Query hooks for Product Mixtures
import {
  useProductMixturesQuery,
  useCreateProductMixtureMutation,
  useUpdateProductMixtureMutation,
  useDeleteProductMixtureMutation,
  ProductMixture,
  AddProductMixtureCmd
} from '@api/queries/productMixtures';

// Re-export types for other components
export type { Product } from '@api/queries/products';
export type { ProductMixture } from '@api/queries/productMixtures';

const ProductList: React.FC = () => {
  const { t } = useTranslation();

  // Local UI state for editors
  const [isProductEditorOpen, setIsProductEditorOpen] = useState(false);
  const [isMixtureEditorOpen, setIsMixtureEditorOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedMixture, setSelectedMixture] = useState<ProductMixture | null>(null);

  // TanStack Query for reference data
  const { data: customers = [] } = useCustomersQuery();
  const { data: tanks = [] } = useTanksQuery();

  // TanStack Query for products
  const { data: products = [], isLoading: isLoadingProducts } = useProductsQuery();
  const createProductMutation = useCreateProductMutation();
  const updateProductMutation = useUpdateProductMutation();
  const deleteProductMutation = useDeleteProductMutation();

  // TanStack Query for product mixtures
  const { data: productMixtures = [], isLoading: isLoadingMixtures } = useProductMixturesQuery();
  const createMixtureMutation = useCreateProductMixtureMutation();
  const updateMixtureMutation = useUpdateProductMixtureMutation();
  const deleteMixtureMutation = useDeleteProductMixtureMutation();

  const isSaving =
    createProductMutation.isPending ||
    updateProductMutation.isPending ||
    createMixtureMutation.isPending ||
    updateMixtureMutation.isPending;

  const isLoadingList = isLoadingProducts || isLoadingMixtures;

  const openProductEditor = (product: Product | null = null) => {
    setIsMixtureEditorOpen(false);
    setSelectedMixture(null);
    setSelectedProduct(product);
    setIsProductEditorOpen(true);
  };

  const closeProductEditor = () => {
    setIsProductEditorOpen(false);
    setSelectedProduct(null);
  };

  const openMixtureEditor = (mixture: ProductMixture | null = null) => {
    setIsProductEditorOpen(false);
    setSelectedProduct(null);
    setSelectedMixture(mixture);
    setIsMixtureEditorOpen(true);
  };

  const closeMixtureEditor = () => {
    setIsMixtureEditorOpen(false);
    setSelectedMixture(null);
  };

  const saveProduct = (product: Product) => {
    if (selectedProduct != null) {
      updateProductMutation.mutate(
        { ...selectedProduct, ...product },
        { onSuccess: () => closeProductEditor() }
      );
    } else {
      createProductMutation.mutate(product as Omit<Product, 'id'>, {
        onSuccess: () => closeProductEditor()
      });
    }
  };

  const saveMixture = (productMixture: ProductMixture | AddProductMixtureCmd) => {
    if (selectedMixture != null) {
      updateMixtureMutation.mutate(
        { ...selectedMixture, ...productMixture } as ProductMixture,
        { onSuccess: () => closeMixtureEditor() }
      );
    } else {
      createMixtureMutation.mutate(productMixture as AddProductMixtureCmd, {
        onSuccess: () => closeMixtureEditor()
      });
    }
  };

  const remove = () => {
    if (selectedProduct != null) {
      deleteProductMutation.mutate(selectedProduct.id, {
        onSuccess: () => closeProductEditor()
      });
    } else if (selectedMixture != null) {
      deleteMixtureMutation.mutate(selectedMixture.id, {
        onSuccess: () => closeMixtureEditor()
      });
    }
  };

  const mergedEntries = [
    ...products.filter(i => i.isEnabled).map(i => ({ ...i, productType: ProductType.Product })),
    ...productMixtures
      .filter(i => i.isEnabled)
      .map(i => ({ ...i, productType: ProductType.Mixture, description: `$()` }))
  ];

  const formKey = (selectedProduct && selectedProduct.id) || 'new';
  const formKeyMixture = (selectedMixture && selectedMixture.id) || 'new';

  return (
    <>
      <HeadingRow>
        <Button primary={true} onClick={() => openProductEditor()}>
          {t('add')} {t('product.types.product').toLowerCase()}
        </Button>
        <Button primary={true} onClick={() => openMixtureEditor()}>
          {t('add')} {t('product.types.mixture').toLowerCase()}
        </Button>
        {(isLoadingList || isSaving) && <OperationLoader title={t('updating') + '...'} />}
      </HeadingRow>
      <Layout>
        <MainContent>
          <ContentContainer>
            <Table
              data={mergedEntries}
              columns={[
                {
                  Header: t('product.type'),
                  id: 'type',
                  accessor: 'productType',
                  show: true,
                  maxWidth: 100,
                  Cell: ({ value }) => {
                    return t(`product.${ProductType[value].toLowerCase()}`);
                  },
                  filterMethod: (filter, row) => (filter.value ? row[filter.id] === Number(filter.value) : true),
                  Filter: ({ filter, onChange }) => (
                    <select
                      onChange={event => onChange(event.target.value)}
                      style={{ width: '100%' }}
                      value={filter ? filter.value : ''}
                    >
                      <option value="">{t('all')}</option>
                      <option value={ProductType.Product}>{t('product.types.product')}</option>
                      <option value={ProductType.Mixture}>{t('product.types.mixture')}</option>
                    </select>
                  )
                },
                {
                  Header: 'Id',
                  id: 'id',
                  accessor: 'id',
                  show: false
                },
                {
                  Header: t('product.code'),
                  id: 'code',
                  accessor: 'code',
                  maxWidth: 160
                },
                {
                  Header: t('product.name'),
                  id: 'name',
                  accessor: 'name'
                },
                {
                  Header: t('product.alternative_codes'),
                  id: 'alternativeCodes',
                  accessor: (product: Product) => (product.aliases ? product.aliases.map(i => i.code).join(', ') : '')
                },
                {
                  Header: t('product.customer'),
                  id: 'customerName',
                  accessor: (product: Product | ProductMixture) => {
                    switch (product.productType) {
                      case ProductType.Product:
                        return Object.keys((product as Product).customers).reduce((acc, cur) => {
                          acc = acc ? acc.concat(', ') : '';
                          return acc.concat((product as Product).customers[cur]);
                        }, '');
                      case ProductType.Mixture:
                        return `${(product as ProductMixture).customerName}`;
                      default:
                        return null;
                    }
                  }
                },
                {
                  Header: t('product.description'),
                  id: 'description',
                  accessor: (product: Product | ProductMixture) => {
                    switch (product.productType) {
                      case ProductType.Product:
                        return '';
                      case ProductType.Mixture:
                        return `${(product as ProductMixture).mixtures[0].productName}
                    & ${(product as ProductMixture).mixtures[1].productName}`;
                      default:
                        return null;
                    }
                  }
                },
                {
                  Header: () => (
                    <span>
                      {t('product.density')} <sup>kg/ltr</sup>
                    </span>
                  ),
                  id: 'density',
                  accessor: 'density',
                  maxWidth: 120
                },
                {
                  Header: () => (
                    <span>
                      {t('product.concentration')} <sup>%</sup>
                    </span>
                  ),
                  id: 'concentration',
                  accessor: 'concentration',
                  maxWidth: 130
                },
                {
                  Header: t('product.notes'),
                  id: 'notes',
                  accessor: 'notes',
                  maxWidth: 500
                }
              ]}
              defaultPageSize={25}
              defaultSortColumn="name"
              onRowSelected={(rowInfo: any) => {
                if (rowInfo.original.productType === ProductType.Product) {
                  openProductEditor(rowInfo.original);
                } else {
                  openMixtureEditor(rowInfo.original);
                }
              }}
              rowSelector={i =>
                (isProductEditorOpen && selectedProduct && selectedProduct.id === i.original.id) ||
                (isMixtureEditorOpen && selectedMixture && selectedMixture.id === i.original.id)
              }
            />
          </ContentContainer>
        </MainContent>
        {isProductEditorOpen && (
          <SideBar>
            <ProductEditor
              key={formKey}
              product={selectedProduct}
              save={saveProduct}
              close={closeProductEditor}
              isSaving={isSaving}
              remove={remove}
            />
          </SideBar>
        )}
        {isMixtureEditorOpen && (
          <SideBar>
            <ProductMixtureEditor
              key={formKeyMixture}
              product={selectedMixture}
              products={products}
              customers={customers}
              save={saveMixture}
              close={closeMixtureEditor}
              isSaving={isSaving}
              remove={remove}
              tanks={tanks}
            />
          </SideBar>
        )}
      </Layout>
    </>
  );
};

export default ProductList;
